"use client";

import React, { useEffect, useRef } from "react";
import { useAudioRecorder } from "../../../hooks/ugc/useAudioRecorder";
import { Mic, Pause, Square, Play, RotateCcw, Check, X } from "lucide-react";
import "./AudioRecorder.css";

export type AudioRecorderStatus = "idle" | "recording" | "paused" | "stopped" | "unsupported" | "error";

export interface AudioRecorderProps {
  onSave: (file: File) => void;
  onCancel: () => void;
  maxDurationMs?: number;
  timesliceMs?: number;
  disabled?: boolean;
  className?: string;
}

const msToClock = (ms: number) => {
  const s = Math.floor(ms / 1000);
  const mm = String(Math.floor(s / 60)).padStart(2, "0");
  const ss = String(s % 60).padStart(2, "0");
  return `${mm}:${ss}`;
};

const AudioRecorder: React.FC<AudioRecorderProps> = ({
  onSave,
  onCancel,
  maxDurationMs,
  timesliceMs,
  disabled,
  className,
}) => {
  const { status, error, durationMs, start, pause, resume, stop, reset } =
    useAudioRecorder({ maxDurationMs, timesliceMs });

  // ---- Volume meter (unchanged) ----
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const rafRef = useRef<number | null>(null);
  const activeStreamRef = useRef<MediaStream | null>(null);

  useEffect(() => {
    const startMeter = async () => {
      if (audioCtxRef.current || analyserRef.current) return;
      try {
        const stream = activeStreamRef.current || (await navigator.mediaDevices.getUserMedia({ audio: true }));
        activeStreamRef.current = stream;

        const AudioCtx = (window.AudioContext || (window as any).webkitAudioContext) as typeof AudioContext;
        const audioCtx = new AudioCtx();
        const analyser = audioCtx.createAnalyser();
        analyser.fftSize = 256;
        const source = audioCtx.createMediaStreamSource(stream);
        source.connect(analyser);

        audioCtxRef.current = audioCtx;
        analyserRef.current = analyser;

        const draw = () => {
          rafRef.current = requestAnimationFrame(draw);
          const canvas = canvasRef.current;
          if (!canvas) return;
          const ctx = canvas.getContext("2d");
          if (!ctx) return;

          const bufferLength = analyser.frequencyBinCount;
          const dataArray = new Uint8Array(bufferLength);
          analyser.getByteTimeDomainData(dataArray);

          ctx.clearRect(0, 0, canvas.width, canvas.height);
          let sum = 0;
          for (let i = 0; i < bufferLength; i++) {
            const v = (dataArray[i] - 128) / 128;
            sum += v * v;
          }
          const rms = Math.sqrt(sum / bufferLength);
          const barWidth = Math.max(2, Math.min(canvas.width, rms * canvas.width));
          ctx.fillStyle = getComputedStyle(canvas).getPropertyValue("--meter-color") || "#3BAA75";
          ctx.fillRect(0, 0, barWidth, canvas.height);
        };
        draw();
      } catch {
        // ignore meter if permission denied
      }
    };

    const stopMeter = () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      if (audioCtxRef.current && audioCtxRef.current.state !== "closed") {
        audioCtxRef.current.close().catch(() => {});
      }
      audioCtxRef.current = null;
      analyserRef.current = null;
    };

    if (status === "recording") startMeter();
    else stopMeter();

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      if (audioCtxRef.current && audioCtxRef.current.state !== "closed") {
        audioCtxRef.current.close().catch(() => {});
      }
      audioCtxRef.current = null;
      analyserRef.current = null;
    };
  }, [status]);

  // IMPORTANT: await stop() to ensure final chunk is collected
  const handleStopAndSave = async () => {
    const f = await stop();
    if (f && f.size > 0) onSave(f);
    reset();
  };

  const allDisabled = !!disabled || status === "error" || status === "unsupported";

  return (
    <div className={`rec-mini ${className || ""}`}>
      <div className="rec-top">
        <div className="rec-time">{msToClock(durationMs)}</div>
        <div className={`rec-dot ${status}`} />
      </div>

      <div className="rec-meter">
        <canvas ref={canvasRef} width={260} height={8} />
      </div>

      <div className="rec-controls" role="group" aria-label="Recorder controls">
        <button className="ic-btn ghost" onClick={onCancel} aria-label="Cancel" title="Cancel">
          <X size={24} />
        </button>

        <div>
          {status === "idle" && (
            <button className="ic-btn primary" onClick={start} disabled={allDisabled} aria-label="Start" title="Start">
              <Mic size={24} />
            </button>
          )}

          {status === "recording" && (
            <>
              <button className="ic-btn" onClick={pause} disabled={allDisabled} aria-label="Pause" title="Pause">
                <Pause size={24} />
              </button>
              <button
                className="ic-btn danger"
                onClick={handleStopAndSave}
                disabled={allDisabled}
                aria-label="Stop & Save"
                title="Stop & Save"
              >
                <Square size={24} />
              </button>
            </>
          )}

          {status === "paused" && (
            <>
              <button className="ic-btn" onClick={resume} disabled={allDisabled} aria-label="Resume" title="Resume">
                <Play size={24} />
              </button>
              <button
                className="ic-btn danger"
                onClick={handleStopAndSave}
                disabled={allDisabled}
                aria-label="Stop & Save"
                title="Stop & Save"
              >
                <Square size={24} />
              </button>
            </>
          )}

          {(status === "stopped" || status === "error" || status === "unsupported") && (
            <button className="ic-btn" onClick={reset} aria-label="Reset" title="Reset">
              <RotateCcw size={24} />
            </button>
          )}
        </div>

        <button
          className="ic-btn ok"
          onClick={handleStopAndSave}  // await inside
          aria-label="Confirm"
          title="Confirm"
        >
          <Check size={24} />
        </button>
      </div>

      {error && <div className="rec-err" role="alert">{error}</div>}
    </div>
  );
};

export default AudioRecorder;
